import React from 'react';
import { get, toInteger, debounce,  merge } from 'lodash';

// React view component to render
import { callWidgetCategoryReasonSummaryList as CallWidgetCategoryReasonSummaryListView } from 'ui/views/widgets/callWidgetCategoryReasonSummaryList';
import { CallWidgetCategoryReasonSummaryService } from 'services/callWidgetCategoryReasonSummary';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import * as errorOverlay from 'utils/errorOverlay';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { CallWidgetCategoryReasonSummaryList } from 'ui/containers/CallWidgetCategoryReasonSummaryList';
 *
 * // render
 * <CallWidgetCategoryReasonSummaryList propName='propValue'/>
 */
export class CallWidgetCategoryReasonSummaryList extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        this.callWidgetCategoryReasonSummaryService = new CallWidgetCategoryReasonSummaryService();
        this.handlePagination = this.handlePagination.bind( this );
        this.fetchTableItems = debounce( this.fetchTableItems, 300 ).bind( this );


        // static component data
        this.data = {
            paginationConfig: get( props, 'paginationConfig', {} ),
        };

        // table data items (rows)
        const items = get( props, 'items', [] );

        // component state
        this.state = {
            isInitialData: true,
            isFetching: false,
            fetchingType: 'pagination',
            networkStatus: null,
            disablePagination: true,
            requestPayload: this.props.requestPayload,

            // table data
            items,
            pagination: get( props, 'pagination', {} ),

        };
    }

     /**
     * @desc Handle pagination
     */
    handlePagination() {
        log.debug( 'CallWidgetCategoryReasonSummaryList.handlePagination()' );

        // if request is already in progress or pagination disabled, ignore pagination
        if( this.state.isFetching || this.state.disablePagination ) {
            return;
        }

        // increment start index
        const nextStartIndex = toInteger( get( this.state, 'pagination.startIndex', 0 ) ) + toInteger( get( this.state, 'pagination.chunkSize', 0 ) );
        
        // update pagination start index (startIndex)
        this.setState( {
            isFetching: true,
            fetchingType: 'pagination',
            pagination: {
                ...this.state.pagination,
                startIndex: nextStartIndex, // update start index
            }
        }, () => this.fetchTableItems( { url: get( this.data, 'paginationConfig.url', {} ), paginate: true } ) );
    }


    /**
     * @desc Fetch new table items based on search, filter and sort criteria.
     */
    async fetchTableItems( { url, paginate = false } = {} ) {
        log.debug( 'CallWidgetCategoryReasonSummaryList.fetchTableItems()' );

        // cancel ongoing request
        this.callWidgetCategoryReasonSummaryService.cancel();

        // fetch table items from the service
        const { status, data } = await this.callWidgetCategoryReasonSummaryService.fetchComponentData( {
            url,
            data: merge( {
                data: {
                    ...this.state.pagination, // pagination
                    ...this.props.requestPayload?.data
                },
                context: { ...this.props.requestPayload?.context }
            }, this.props.requestPayload ),
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.CANCELLED === status ) {
           return null; // ignore on request cancellations
        } else if( SERVICE_RESPONSE_STATUS.FAILURE === status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            }, () => {
                errorOverlay.open();
            } );
        } else {
            const { items } = data;
            
            // append items based on pagination
            let _items = items;
            if( paginate ) {
                _items = this.state.items.concat( _items );
            }

            // update state
            this.setState( {
                isInitialData: false,
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                disablePagination: items.length < get( this.state, 'pagination.chunkSize' ), // disable pagination if items returned are less than chunk size
                items: _items,
            } );
        }
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <CallWidgetCategoryReasonSummaryListView
            { ...this.state }
            options = { this.props.options }
            className={ this.props.className }
            onPagination={ this.handlePagination }
             />
        );
    }
}

// set display name
CallWidgetCategoryReasonSummaryList.displayName = 'CallWidgetCategoryReasonSummaryList';

// set default props
CallWidgetCategoryReasonSummaryList.defaultProps = {};

// prop types
CallWidgetCategoryReasonSummaryList.propTypes = {};
